
/**
 * Parse an Excel date into YYYY-MM-DD format
 * Handles several common formats:
 * - Excel serial numbers (e.g., 45711)
 * - DD/MM/YYYY
 * - MM/DD/YYYY
 * - YYYY/MM/DD
 * - Other date strings that JavaScript's Date can parse
 */
export const parseExcelDate = (excelDate: any): string | null => {
  if (!excelDate) return null;
  
  try {
    // Case 1: If it's already in YYYY-MM-DD format
    if (typeof excelDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(excelDate)) {
      return excelDate;
    }
    
    // Case 2: If it's an Excel serial number (numeric value)
    if (typeof excelDate === 'number' || /^\d+$/.test(excelDate.toString())) {
      const excelEpoch = new Date(1899, 11, 30);
      const serialValue = typeof excelDate === 'number' ? excelDate : parseInt(excelDate, 10);
      
      // Excel has a leap year bug where it thinks 1900 is a leap year
      // If date is after February 28, 1900, subtract 1 day
      const daysSince1900 = serialValue > 60 ? serialValue - 1 : serialValue;
      
      const millisecondsToAdd = daysSince1900 * 24 * 60 * 60 * 1000;
      const jsDate = new Date(excelEpoch.getTime() + millisecondsToAdd);
      
      return formatDateToYYYYMMDD(jsDate);
    }
    
    // Case 3: If it's in other formats like DD/MM/YYYY, MM/DD/YYYY, etc.
    if (typeof excelDate === 'string') {
      const parts = excelDate.split(/[-\/\.]/);
      
      if (parts.length === 3) {
        let year, month, day;
        
        if (parts[0].length === 4) {
          year = parseInt(parts[0], 10);
          month = parseInt(parts[1], 10) - 1;
          day = parseInt(parts[2], 10);
        } 
        else if (parts[2].length === 4) {
          year = parseInt(parts[2], 10);
          if (parseInt(parts[0], 10) > 12) {
            day = parseInt(parts[0], 10);
            month = parseInt(parts[1], 10) - 1;
          } else {
            month = parseInt(parts[0], 10) - 1;
            day = parseInt(parts[1], 10);
          }
        } else {
          const jsDate = new Date(excelDate);
          if (!isNaN(jsDate.getTime())) {
            return formatDateToYYYYMMDD(jsDate);
          }
          return null;
        }
        
        const jsDate = new Date(year, month, day);
        if (!isNaN(jsDate.getTime())) {
          return formatDateToYYYYMMDD(jsDate);
        }
      }
      
      const jsDate = new Date(excelDate);
      if (!isNaN(jsDate.getTime())) {
        return formatDateToYYYYMMDD(jsDate);
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error parsing date:', error, excelDate);
    return null;
  }
};

export const formatDateToYYYYMMDD = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
